// ============================================
// SubscriptionPlans Component
// ============================================
// Display available subscription plans
// Allow users to select and upgrade/downgrade
// ============================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaHome, FaReceipt, FaCreditCard } from 'react-icons/fa';
import './SubscriptionPlans.css';

const SubscriptionPlans = ({ tenantId, currentPlanId, onPlanChanged }) => {
  const navigate = useNavigate();
  const params = useParams();
  const actualTenantId = tenantId || params.tenant;
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    fetchData();
  }, [actualTenantId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.allSettled([
        axios.get('/api/billing/plans'),
        axios.get(`/api/billing/subscription/${actualTenantId}`)
      ]);

      if (plansRes.status === 'fulfilled') {
        setPlans(plansRes.value.data.plans || []);
      } else {
        setError('Failed to load subscription plans');
      }

      if (subRes.status === 'fulfilled' && subRes.value.data.success) {
        setSubscription(subRes.value.data.subscription);
      }
      // No subscription is fine - user can subscribe
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const activePlanId = currentPlanId || subscription?.plan_id;

  const handleSelectPlan = async (newPlanId) => {
    if (newPlanId === activePlanId) {
      return;
    }

    const newPlan = plans.find(p => p.id === newPlanId);
    if (!newPlan) return;

    // If no existing subscription, create one
    if (!activePlanId || !subscription) {
      const trialText = newPlan.trial_days > 0
        ? `You'll get a ${newPlan.trial_days}-day free trial.`
        : `You'll be charged $${newPlan.price_monthly}/month.`;

      if (!window.confirm(`Subscribe to the ${newPlan.name} plan? ${trialText}`)) {
        return;
      }

      try {
        setChanging(true);
        const res = await axios.post(`/api/billing/subscription/${actualTenantId}`, {
          planId: newPlanId
        });

        if (res.data.success) {
          if (onPlanChanged) onPlanChanged();
          // Navigate to payment methods with plan info for immediate payment
          navigate(`/${actualTenantId}/billing/payment-methods`, {
            state: {
              planId: newPlanId,
              planName: newPlan.name,
              planPrice: newPlan.price_monthly,
              purpose: 'subscription_payment',
              message: `Subscribed to ${newPlan.name}! ${newPlan.trial_days > 0 ? `Your ${newPlan.trial_days}-day free trial has started. ` : ''}Add a payment method to continue.`
            }
          });
        }
      } catch (err) {
        console.error('Error creating subscription:', err);
        alert(err.response?.data?.message || 'Failed to create subscription. Please try again.');
      } finally {
        setChanging(false);
      }
      return;
    }

    // Changing existing plan
    const currentPlan = plans.find(p => p.id === activePlanId);
    const isUpgrade = currentPlan ? newPlan.price_monthly > currentPlan.price_monthly : true;
    const action = isUpgrade ? 'upgrade to' : 'downgrade to';

    if (!window.confirm(`Are you sure you want to ${action} the ${newPlan.name} plan? Changes will take effect immediately and you may be charged prorated amounts.`)) {
      return;
    }

    try {
      setChanging(true);
      await axios.put(`/api/billing/subscription/${actualTenantId}/plan`, {
        newPlanId: newPlanId
      });

      if (onPlanChanged) onPlanChanged();

      // Navigate to payment with updated plan info
      navigate(`/${actualTenantId}/billing/payment-methods`, {
        state: {
          planId: newPlanId,
          planName: newPlan.name,
          planPrice: newPlan.price_monthly,
          purpose: 'plan_change',
          message: `Successfully ${isUpgrade ? 'upgraded' : 'downgraded'} to ${newPlan.name}! Prorated charges will apply.`
        }
      });
    } catch (err) {
      console.error('Error changing plan:', err);
      alert('Failed to change plan. Please try again.');
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return <div className="plans-loading">Loading plans...</div>;
  }

  if (error) {
    return <div className="plans-error">{error}</div>;
  }

  const formatFeatureLimit = (value, isUnlimited) => {
    if (isUnlimited) return 'Unlimited';
    return value;
  };

  return (
    <div className="subscription-plans" style={{ position: 'relative', paddingBottom: '72px' }}>
      <h2>Subscription Plans</h2>
      <p className="plans-description">
        Choose the plan that best fits your needs. You can upgrade or downgrade at any time.
      </p>

      <div className="plans-grid">
        {plans.map(plan => {
          const isCurrentPlan = plan.id === activePlanId;
          const currentPlanPrice = plans.find(p => p.id === activePlanId)?.price_monthly || 0;
          const isUpgrade = plan.price_monthly > currentPlanPrice;

          return (
            <div
              key={plan.id}
              className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}
            >
              {isCurrentPlan && (
                <div className="current-badge">Current Plan</div>
              )}

              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price-amount">${plan.price_monthly}</span>
                  <span className="price-period">/month</span>
                </div>
              </div>

              <div className="plan-features">
                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_users, plan.is_unlimited_users)} 
                    {plan.is_unlimited_users ? ' users' : ` user${plan.included_users !== 1 ? 's' : ''}`}
                  </span>
                </div>

                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_integrations, plan.is_unlimited_integrations)} 
                    {plan.is_unlimited_integrations ? ' integrations' : ' integration' + (plan.included_integrations !== 1 ? 's' : '')}
                  </span>
                </div>

                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_posts_daily, plan.is_unlimited_posts)}
                    {plan.is_unlimited_posts ? ' posts/month' : ` post${plan.included_posts_daily !== 1 ? 's' : ''}/month`}
                  </span>
                </div>

                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_posts_monthly, plan.is_unlimited_posts)}
                    {plan.is_unlimited_posts ? ' scheduled posts/month' : ` scheduled post${plan.included_posts_monthly !== 1 ? 's' : ''}/month`}
                  </span>
                </div>

                {plan.extra_user_price > 0 && !plan.is_unlimited_users && (
                  <div className="feature-item extra-pricing">
                    <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                    <span>${plan.extra_user_price}/month per additional user</span>
                  </div>
                )}

                <div className="feature-item trial-info-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span>{plan.trial_days}-day free trial</span>
                </div>
              </div>

              <div className="plan-footer">
                {!isCurrentPlan && (
                  <button
                    className={`btn-plan ${!activePlanId ? 'btn-subscribe' : isUpgrade ? 'btn-upgrade' : 'btn-downgrade'}`}
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={changing}
                  >
                    {changing ? 'Processing...' : !activePlanId ? 'Subscribe' : isUpgrade ? 'Upgrade' : 'Downgrade'}
                  </button>
                )}
                {isCurrentPlan && (
                  <button className="btn-plan btn-current" disabled>
                    Current Plan
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="plans-footer">
        <p className="plans-note">
          All plans include core features. Extra users beyond the included amount are billed at
          $1/month each (except Enterprise which has unlimited users).
        </p>
      </div>

      {/* App Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaReceipt />,
            label: "Billing",
            onClick: () => navigate(`/${actualTenantId}/billing`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${actualTenantId}/dashboard`),
          },
          {
            icon: <FaCreditCard />,
            label: "Payment",
            onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
          },
        ]}
      />
    </div>
  );
};

export default SubscriptionPlans;